import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Upload, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const POLICY_TYPES = {
  ETHICS_CODE: "Codice Etico", MODEL_231: "Modello 231",
  ANTI_CORRUPTION_POLICY: "Policy Anticorruzione", WHISTLEBLOWING_POLICY: "Policy Whistleblowing",
  PRIVACY_POLICY: "Policy Privacy", CYBER_POLICY: "Policy Cybersecurity",
  CONFLICT_OF_INTEREST_POLICY: "Policy Conflitti di interesse", SUPPLIER_CODE: "Codice Fornitori",
  BUSINESS_CONTINUITY_PLAN: "Piano di continuità operativa", ESG_POLICY: "Policy ESG",
};
const POLICY_STATUS = {
  DRAFT: ["Bozza", "bg-slate-100 text-slate-700"],
  ACTIVE: ["Attiva", "bg-green-100 text-green-800"],
  UNDER_REVIEW: ["In riesame", "bg-amber-100 text-amber-800"],
  EXPIRED: ["Scaduta", "bg-red-100 text-red-800"],
  REPLACED: ["Sostituita", "bg-slate-200 text-slate-500"],
};

// Governance Policy Library (Fase 7): documenti classificati per tipo con versionamento.
// Un file caricato NON è una policy attiva: lo stato va confermato esplicitamente.
export default function GovernancePolicies({ onChanged }) {
  const { companyId, period, can } = useTenant();
  const [data, setData] = useState(null);
  const [docType, setDocType] = useState("ETHICS_CODE");
  const [version, setVersion] = useState("");
  const [approvalDate, setApprovalDate] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canWrite = can("esg:write") && period?.status !== "LOCKED";
  const base = `/companies/${companyId}/periods/${period?.id}`;

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data: d } = await api.get(`${base}/governance/policies`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [companyId, period?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", docType);
      if (version) fd.append("policy_version", version);
      if (approvalDate) fd.append("approval_date", approvalDate);
      if (reviewDate) fd.append("last_review_date", reviewDate);
      await api.post(`${base}/documents`, fd);
      toast.success(`"${file.name}" caricato come ${POLICY_TYPES[docType]} (stato: Bozza — attivalo dopo la verifica del contenuto)`);
      setVersion("");
      setApprovalDate("");
      setReviewDate("");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setStatus = async (doc, status) => {
    try {
      await api.patch(`${base}/documents/${doc.id}/policy`, { policy_status: status });
      toast.success(status === "ACTIVE" ? "Policy attivata" : "Stato aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="gov-policies">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Upload className="w-4 h-4" aria-hidden="true" /> Carica una policy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="w-[240px] h-9" data-testid="policy-type-select" aria-label="Tipo policy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(POLICY_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="text" value={version} onChange={(e) => setVersion(e.target.value)}
              placeholder="Versione (es. 2.0)" maxLength={20}
              className="h-9 w-32 rounded-md border border-slate-300 px-3 text-sm"
              data-testid="policy-version-input" aria-label="Versione policy" />
            <input type="date" value={approvalDate} onChange={(e) => setApprovalDate(e.target.value)}
              className="h-9 rounded-md border border-slate-300 px-3 text-sm"
              data-testid="policy-approval-date" aria-label="Data approvazione" />
            <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)}
              className="h-9 rounded-md border border-slate-300 px-3 text-sm"
              data-testid="policy-review-date" aria-label="Data ultimo riesame" />
          </div>
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="policy-file-input" />
          <Button size="sm" disabled={!canWrite || uploading} onClick={() => fileRef.current?.click()}
            className="bg-green-700 hover:bg-green-800 text-white" data-testid="policy-upload-button">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" />
              : <Upload className="w-4 h-4 mr-1" aria-hidden="true" />}
            Carica documento policy
          </Button>
          <p className="text-xs text-slate-500">
            Il caricamento crea sempre una <strong>Bozza</strong>: la policy diventa "Attiva" solo dopo conferma esplicita.
          </p>
        </CardContent>
      </Card>

      {data?.alerts?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="policy-alerts">
          {data.alerts.map((a, i) => (
            <p key={i} className="text-xs text-amber-900 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />{a.message}
            </p>
          ))}
        </div>
      )}

      <div className="space-y-3" data-testid="policy-library">
        {data === null ? (
          <p className="text-sm text-slate-500">Caricamento…</p>
        ) : data.policies.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid="policy-empty">
            Nessuna policy caricata. Carica Codice Etico, Modello 231, policy anticorruzione o altre policy ESG.
          </p>
        ) : (
          data.policies.map((group) => (
            <Card key={group.document_type} className="border-slate-200"
              data-testid={`policy-group-${group.document_type}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4" aria-hidden="true" />
                  {POLICY_TYPES[group.document_type] || group.document_type}
                  <Badge variant="outline" className="text-xs">{group.versions.length} versione/i</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-slate-100">
                  {group.versions.map((doc) => {
                    const st = POLICY_STATUS[doc.policy_status] || POLICY_STATUS.DRAFT;
                    return (
                      <li key={doc.id} className="py-2.5 flex flex-wrap items-center gap-2"
                        data-testid={`policy-version-${doc.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                          <p className="text-xs text-slate-500">
                            {doc.policy_version && `v${doc.policy_version} · `}
                            {doc.approval_date && `approvata il ${doc.approval_date} · `}
                            {doc.last_review_date && `riesame ${doc.last_review_date} · `}
                            caricata il {new Date(doc.created_at).toLocaleDateString("it-IT")}
                          </p>
                        </div>
                        <Badge className={st[1]} data-testid={`policy-status-${doc.id}`}>{st[0]}</Badge>
                        {canWrite && doc.policy_status !== "ACTIVE" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => setStatus(doc, "ACTIVE")}
                            data-testid={`policy-activate-${doc.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Attiva
                          </Button>
                        )}
                        {canWrite && doc.policy_status === "ACTIVE" && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500"
                            onClick={() => setStatus(doc, "UNDER_REVIEW")}
                            data-testid={`policy-review-${doc.id}`}>
                            Metti in riesame
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
